import { API_BASE_URL } from "@kupikupi/brand";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getInboxUnread,
  getInboxUnreadCount,
  listInboxMessages,
  popUpInboxMessage,
  processInboxMessage,
} from "../endpoints/inbox";
import type { InboxMessage, InboxMessageRaw, InboxPaged, InboxUnread } from "../types/inbox";

const KEY = ["inbox"] as const;

export function useInboxMessages(
  authToken: string | null,
  input: { pageNum: number; pageSize: number; messageType?: number } = {
    pageNum: 1,
    pageSize: 20,
  },
) {
  return useQuery<InboxPaged>({
    queryKey: [...KEY, "list", input.pageNum, input.pageSize, input.messageType],
    enabled: !!authToken,
    queryFn: () => {
      if (!authToken) throw new Error("NO_TOKEN");
      return listInboxMessages({ baseUrl: API_BASE_URL }, authToken, input);
    },
    staleTime: 30_000,
  });
}

export function useInboxUnread(authToken: string | null) {
  return useQuery<InboxUnread>({
    queryKey: [...KEY, "unread"],
    enabled: !!authToken,
    queryFn: () => {
      if (!authToken) throw new Error("NO_TOKEN");
      return getInboxUnread({ baseUrl: API_BASE_URL }, authToken);
    },
    staleTime: 30_000,
  });
}

export function useInboxUnreadCount(authToken: string | null) {
  return useQuery<number>({
    queryKey: [...KEY, "unread-count"],
    enabled: !!authToken,
    queryFn: () => {
      if (!authToken) throw new Error("NO_TOKEN");
      return getInboxUnreadCount({ baseUrl: API_BASE_URL }, authToken);
    },
    staleTime: 30_000,
  });
}

export function usePopUpInboxMessage(authToken: string | null) {
  return useMutation<InboxMessage | null>({
    mutationFn: async () => {
      if (!authToken) throw new Error("NO_TOKEN");
      return popUpInboxMessage({ baseUrl: API_BASE_URL }, authToken);
    },
  });
}

export function useProcessInboxMessage(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (message: InboxMessageRaw) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return processInboxMessage({ baseUrl: API_BASE_URL }, authToken, message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
